import { useQueryClient } from '@tanstack/react-query';
import React, { FC, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { ContentLoadError, Flex, PageWrapper, Spinner, StepNavigation } from '../../../components';
import { useHeroToast, useWarmup } from '../../../hooks';
import {
  useGetDeviceDocumentFromFirestore,
  useQueryHelper,
  useUpdateDevice,
} from '../../../queries';
import { DeviceDocumentFirestoreRequest } from '../../../types';
import { CreateDeviceForm } from '../components';

export const EditDevice: FC = () => {
  const [activeStep, setActiveStep] = useState<number>(1);

  // Cold start funkcji v3 — budzimy ją przy wejściu na formularz edycji.
  useWarmup('v3-updateDeviceAdmin');

  const { mutate, isPending } = useUpdateDevice();

  const { id } = useParams();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { showPromise, showError } = useHeroToast();

  const deviceDocument = useGetDeviceDocumentFromFirestore(id);
  const { data: device, refetch } = deviceDocument;
  const { isLoading, isError, errorMessage } = useQueryHelper({
    queries: [deviceDocument],
  });

  const handleOnSubmit = (deviceId : string, data: DeviceDocumentFirestoreRequest) : void => {
    showPromise(
      () => new Promise(
        (resolve, reject) => {
          mutate(
            { podId: deviceId, data },
            {
              onSuccess: resolve,
              onError: reject,
            },
          );
        },
      ),
      <Spinner />,
    ).then(() => {
      navigate('/devices');
      queryClient.invalidateQueries({ queryKey: ['pods', deviceId] });
    }).catch((error) => {
      showError(error.message);
    });
  };

  if (isLoading) {
    return <Spinner global />;
  }

  if (isError) {
    return (
      <PageWrapper>
        <ContentLoadError onRefreshClicked={() => { refetch(); }} message={errorMessage} />
      </PageWrapper>
    );
  }

  return (
    <Flex direction="row" gap="small" fullHeight>
      <PageWrapper>
        <CreateDeviceForm
          onSubmit={(e) => handleOnSubmit(id || '', e)}
          activeStep={activeStep}
          onStepClick={setActiveStep}
          isEdit
          initialValues={device}
        />
      </PageWrapper>
      <StepNavigation
        isLoading={isPending}
        isDisabled={isPending}
        showSubmit={activeStep === 5}
        skipAndSubmit
        form="form-create-device"
        previous={{
          showButton: activeStep > 1,
          label: 'Back',
          onClick: () => (setActiveStep(activeStep - 1)),
        }}
        next={{
          label: 'Next',
          onClick: () => (
            setActiveStep(activeStep + 1)
          ),
        }}
      />
    </Flex>
  );
};
